import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import type { Asset, HolderInput } from '@khadi/shared';
import { api, ApiError } from '../lib/api';
import { useModels, useSuppliers } from '../lib/queries';
import { PageHeader, StatusPill } from '../components/ui';
import { HolderPicker, isHolderComplete } from '../components/HolderPicker';
import { LabelSheet } from '../components/LabelSheet';

/**
 * Massenerfassung für Sammellieferungen — etwa die sechs iPhone 17e oder
 * die elf Pro-16-Plus-Notebooks aus dem Bestand.
 *
 * Seriennummern untereinander einfügen, gemeinsames Modell und Ziel wählen,
 * vor dem Anlegen prüfen.
 */
export function BulkCapturePage() {
  const queryClient = useQueryClient();
  const models = useModels();
  const suppliers = useSuppliers();

  const [raw, setRaw] = useState('');
  const [modelId, setModelId] = useState(0);
  const [target, setTarget] = useState<HolderInput>({ type: 'none' });
  const [purchaseDate, setPurchaseDate] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [supplierId, setSupplierId] = useState(0);
  const [created, setCreated] = useState<Asset[]>([]);
  const [skipped, setSkipped] = useState<{ serial: string; reason: string }[]>([]);
  const [labelOpen, setLabelOpen] = useState(false);

  const serials = raw
    .split(/[\n,;\t]+/)
    .map((value) => value.trim())
    .filter(Boolean);

  const body = () => ({
    serials,
    model_id: modelId,
    holder: isHolderComplete(target) ? target : { type: 'none' as const },
    purchase_date: purchaseDate || undefined,
    order_number: orderNumber.trim() || undefined,
    supplier_id: supplierId || undefined,
    skip_existing: true,
  });

  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      api<{ dry_run: boolean; created: Asset[]; skipped: { serial: string; reason: string }[] }>(
        '/assets/bulk',
        { method: 'POST', body: { ...body(), dry_run: dryRun } },
      ),
    onSuccess: (result) => {
      setSkipped(result.skipped);
      if (result.dry_run) {
        toast.info(
          `${result.created.length} ${result.created.length === 1 ? 'Gerät' : 'Geräte'} würden angelegt`,
        );
        return;
      }
      setCreated(result.created);
      setRaw('');
      void queryClient.invalidateQueries({ queryKey: ['assets'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success(`${result.created.length} Geräte angelegt`);
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Die Erfassung ist fehlgeschlagen'),
  });

  const ready = serials.length > 0 && modelId > 0;

  return (
    <div className="mx-auto max-w-2xl">
      <Link
        to="/erfassen"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-[var(--color-fg3)] no-underline hover:text-[var(--color-accent)]"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
        Einzelerfassung
      </Link>

      <PageHeader
        eyebrow="Sammellieferung"
        title="Mehrere Geräte erfassen"
        subtitle="Seriennummern aus dem Lieferschein einfügen — eine pro Zeile."
      />

      <div className="space-y-5">
        <section className="surface animate-fade-up pad">
          <label className="label" htmlFor="bulk-serials">
            Seriennummern
            {serials.length > 0 && (
              <span className="tabular ml-2 font-normal text-[var(--color-fg3)] normal-case">
                {serials.length} erkannt
              </span>
            )}
          </label>
          <textarea
            id="bulk-serials"
            rows={8}
            className="field tabular"
            value={raw}
            onChange={(event) => setRaw(event.target.value)}
            placeholder={'CJQ0LHG6WR\nF4H9K2M1PQ\n…'}
          />
        </section>

        <section className="surface animate-fade-up pad" style={{ animationDelay: '40ms' }}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="label" htmlFor="bulk-model">
                Modell (für alle)
              </label>
              <select
                id="bulk-model"
                className="field"
                value={modelId}
                onChange={(event) => setModelId(Number(event.target.value))}
                required
              >
                <option value={0}>— bitte wählen —</option>
                {models.data?.rows.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label" htmlFor="bulk-date">
                Kaufdatum
              </label>
              <input
                id="bulk-date"
                type="date"
                className="field"
                value={purchaseDate}
                onChange={(event) => setPurchaseDate(event.target.value)}
              />
            </div>

            <div>
              <label className="label" htmlFor="bulk-order">
                Bestellnummer
              </label>
              <input
                id="bulk-order"
                className="field"
                value={orderNumber}
                onChange={(event) => setOrderNumber(event.target.value)}
              />
            </div>

            <div className="sm:col-span-2">
              <label className="label" htmlFor="bulk-supplier">
                Lieferant
              </label>
              <select
                id="bulk-supplier"
                className="field"
                value={supplierId}
                onChange={(event) => setSupplierId(Number(event.target.value))}
              >
                <option value={0}>— keiner —</option>
                {suppliers.data?.rows.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        <section className="surface animate-fade-up pad" style={{ animationDelay: '80ms' }}>
          <span className="label">Ziel (für alle)</span>
          <HolderPicker value={target} onChange={setTarget} />
        </section>

        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => run.mutate(true)}
            disabled={!ready || run.isPending}
            className="btn btn-ghost btn-lg flex-1"
          >
            <ListChecks className="h-4 w-4" aria-hidden />
            Erst prüfen
          </button>
          <button
            type="button"
            onClick={() => run.mutate(false)}
            disabled={!ready || run.isPending}
            className="btn btn-primary btn-lg flex-1"
          >
            {run.isPending ? 'Läuft …' : `${serials.length} Geräte anlegen`}
          </button>
        </div>

        {skipped.length > 0 && (
          <section className="surface animate-fade-up border-[var(--color-khadi-saffron-400)] p-5">
            <h2 className="font-[family-name:var(--font-display)] mb-3 text-xl">Übersprungen</h2>
            <ul className="space-y-1 text-sm text-[var(--color-fg2)]">
              {skipped.map((entry) => (
                <li key={entry.serial}>
                  <span className="tabular font-semibold">{entry.serial}</span> — {entry.reason}
                </li>
              ))}
            </ul>
          </section>
        )}

        {created.length > 0 && (
          <section className="surface animate-fade-up pad">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-[family-name:var(--font-display)] text-xl">
                {created.length} angelegt
              </h2>
              <button type="button" onClick={() => setLabelOpen(true)} className="btn btn-primary btn-sm">
                Etiketten drucken
              </button>
            </div>
            <ul className="stagger space-y-2">
              {created.map((asset) => (
                <li key={asset.id}>
                  <Link
                    to="/geraete/$assetId"
                    params={{ assetId: String(asset.id) }}
                    className="flex items-center justify-between gap-3 rounded-[var(--radius-sm)] px-2 py-1.5 text-sm no-underline hover:bg-[var(--color-bg-tinted)]"
                  >
                    <span className="tabular font-semibold">{asset.asset_tag}</span>
                    <span className="tabular truncate text-[var(--color-fg3)]">{asset.serial}</span>
                    <StatusPill name={asset.status.name} archived={asset.status.is_archived} />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {labelOpen && (
        <LabelSheet assetIds={created.map((asset) => asset.id)} onClose={() => setLabelOpen(false)} />
      )}
    </div>
  );
}
